/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { match } from "@formatjs/intl-localematcher";
import { getTopLevelDomain } from "@wf-components/navigation-consumer";
import Negotiator from "negotiator";
import { NextResponse, type NextRequest } from "next/server";

import { LocaleCookieName, LocaleHeaderName, type LocalePrefixMode } from "../types/locale.types";
import { getLocalePrefixMode } from "../utils/locale-path.utils";
import {
	checkPathnameHasLocale,
	langToLocale,
	pathnameStartsWithLocale,
	pathnameToLocale,
	removePathnameLocale,
	replaceUnsupportedLocale,
} from "../utils/locale.utils";

const getCurrentLocale = (headers: Negotiator.Headers, locales: string[], defaultLocale: string): string => {
	if (!headers) return defaultLocale;
	try {
		const languages = new Negotiator({ headers }).languages();
		return match(languages, locales, defaultLocale);
	} catch {
		//Failed to determine locale from headers
		return defaultLocale;
	}
};

// Cache for locale mapping regexes to avoid reconstruction on every request
const localeMappingRegexCache = new Map<string, RegExp>();

const getLocaleMappingRegex = (unsupportedLocales: string[]): RegExp => {
	const cacheKey = unsupportedLocales.join("|");
	let regex = localeMappingRegexCache.get(cacheKey);
	if (!regex) {
		regex = new RegExp(`^/(${cacheKey})(/?$|/.+)`, "i");
		localeMappingRegexCache.set(cacheKey, regex);
	}
	return regex;
};

/**
 * Processes locale handling for incoming requests, including:
 * - Custom locale mapping for unsupported ISO 639 codes
 * - Locale validation and redirection
 * - Language parameter handling
 * - Accept-Language header negotiation
 *
 * @param request - The incoming Next.js request
 * @param locales - Array of supported locale codes (e.g., ["en-US", "fr", "de"])
 * @param defaultLocale - Default locale to use as fallback
 * @param unsupportedLocaleMapping - Optional mapping of unsupported locale codes to supported ones.
 *   Example: `{ "en": "en-US", "zh": "zh-CN" }`. Define this in your site's `i18n/config.ts`
 *   to automatically redirect legacy/unsupported locales.
 *   Values must be type-safe: `Record<string, typeof locales[number]>`.
 * @param siteIdHeaders - Optional headers to forward in rewrite responses. Only used in `"never"` mode.
 *
 * The locale prefix mode (`always` | `as-needed` | `never`) is read from
 * `process.env.NEXT_PUBLIC_LOCALE_MODE` (defaults to `"always"`).
 *
 * @example
 * // i18n/config.ts
 * export const unsupportedLocaleMapping: Record<string, typeof locales[number]> = {
 *   en: "en-US",
 *   zh: "zh-CN",
 * };
 *
 * // middleware.ts
 * const localeRes = processLocale(request, locales, defaultLocale, unsupportedLocaleMapping);
 */
export interface ProcessLocaleOptions {
	/** Overrides the build-wide locale mode for a tenant in a shared runtime. */
	localePrefix?: LocalePrefixMode;
	/**
	 * `"never"` mode only. When true, the resolved locale is forwarded to Server
	 * Components on the {@link LocaleHeaderName} request header instead of the
	 * request being rewritten to `/{locale}{pathname}`.
	 *
	 * Opt in only for sites whose `app/` directory has no `[lang]` segment —
	 * without the rewrite there is nothing to populate `params.lang`. Sites still
	 * using `app/[lang]/*` must leave this off and keep the rewrite.
	 *
	 * Removing the rewrite also keeps the request URL and the rendered route
	 * identical, which avoids a Next.js prefetch defect where a `basePath` pads
	 * the URL enough for rewrite detection to miss the mismatch (MW-13262).
	 */
	localeViaHeader?: boolean;
}

/** Guard state so a misconfiguration is reported once, not once per request. */
let hasWarnedLocaleViaHeader = false;

/**
 * `localeViaHeader` only takes effect in `"never"` mode, and a site using it has no
 * `[lang]` segment to fall back on. If `NEXT_PUBLIC_LOCALE_MODE` is ever lost or
 * misspelled, `getLocalePrefixMode()` silently returns `"always"` and the middleware
 * starts redirecting `/news` → `/en-US/news`, which now only matches the root
 * `[...slug]` catch-all as `slug=["en-US","news"]` — every URL degrades to a
 * wrong-or-missing page with nothing surfaced anywhere.
 *
 * Log loudly rather than throw: the failure is already total, and throwing inside
 * middleware would turn a diagnosable degradation into a 500 on every request.
 */
function warnIfLocaleViaHeaderUnsupported(localePrefix: LocalePrefixMode, options?: ProcessLocaleOptions): void {
	if (!options?.localeViaHeader || localePrefix === "never" || hasWarnedLocaleViaHeader) return;

	hasWarnedLocaleViaHeader = true;
	console.error(
		`[processLocale] localeViaHeader is enabled but NEXT_PUBLIC_LOCALE_MODE resolved to "${localePrefix}", ` +
			`not "never". The locale header will not be set and URLs will be redirected to locale-prefixed paths ` +
			`that a site without a [lang] segment cannot serve. Set NEXT_PUBLIC_LOCALE_MODE=never for this site.`,
	);
}

export function processLocale(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
	siteIdHeaders?: Record<string, string>,
	options?: ProcessLocaleOptions,
): Response | NextResponse | void {
	const localePrefix = options?.localePrefix ?? getLocalePrefixMode();
	const { pathname = "/" } = request.nextUrl;

	warnIfLocaleViaHeaderUnsupported(localePrefix, options);

	// If request has "setlang" query param process it and redirect to clean url
	const setLangResponse = handleSetLangParam(request, locales, unsupportedLocaleMapping);
	if (setLangResponse) {
		return setLangResponse;
	}

	// Normalize underscore-format locale prefix (e.g. /en_US/foo → /en-US/foo).
	// Partner systems and legacy bookmarks frequently use POSIX-style codes; without
	// this step they bypass locale-aware pipeline and 404. Mirrors getMappedLocale's
	// underscore handling for `lang` query params and the locale cookie.
	const underscoreResponse = processUnderscoreLocale(request, locales, unsupportedLocaleMapping);
	if (underscoreResponse) {
		return underscoreResponse;
	}

	// Handle "as-needed" locale prefix mode
	// In this mode, the default locale has no URL prefix while other locales do
	if (localePrefix === "as-needed") {
		return processLocaleAsNeeded(request, locales, defaultLocale, unsupportedLocaleMapping);
	}

	// Handle "never" locale prefix mode
	// In this mode, no locale prefixes are used in URLs (domain-based routing)
	if (localePrefix === "never") {
		return processLocaleNever(request, locales, defaultLocale, unsupportedLocaleMapping, siteIdHeaders, options);
	}

	// Default "always" mode - all locales have prefixes
	// Check for custom locale mapping first (e.g., /en/* -> /en-US/*)
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const match = pathname.match(localeRegex);

		if (match) {
			const unsupportedLocale = match[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];

			if (targetLocale && locales.includes(targetLocale)) {
				const rest = match[2] === "/" ? "" : match[2];
				request.nextUrl.pathname = `/${targetLocale}${rest}`;
				return Response.redirect(request.nextUrl, 308);
			}
		}
	}

	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		/**
		 * The pathname has a locale but it's not one of the app's supported locales,
		 *  update the pathname to default locale and redirect.
		 */
		request.nextUrl.pathname = replacedUnsupportedLocale;
		return Response.redirect(request.nextUrl);
	}

	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);

	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam?.length) {
		const localeFromParam = langToLocale(langParam, locales);
		/**
		 * The lang param is present and valid but the pathname doesn't start with locale,
		 *  update the pathname to new locale and redirect.
		 */
		if (typeof localeFromParam === "string" && !pathnameStartsWithLocale(pathname, localeFromParam)) {
			if (pathnameHasLocale) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = `/${localeFromParam}${newPathname}`;
			} else {
				request.nextUrl.pathname = `/${localeFromParam}${pathname}`;
			}
			request.nextUrl.searchParams.delete("lang");
			return Response.redirect(request.nextUrl);
		}
	}

	if (pathnameHasLocale) {
		/**
		 * The pathname has locale but pathname locale case doesn't match,
		 *  update the pathname to correct locale case and redirect.
		 */
		const localeFromPath = pathnameHasLocale && pathnameToLocale(pathname, locales);
		if (typeof localeFromPath === "string" && !pathnameStartsWithLocale(pathname, localeFromPath)) {
			const newPathname = removePathnameLocale(pathname, locales);
			request.nextUrl.pathname = `/${localeFromPath}${newPathname}`;
			return Response.redirect(request.nextUrl);
		}
	} else {
		/**
		 * The lang param isn't present and there isn't a locale in the pathname,
		 *  update the pathname to have a locale from the language headers, and then redirect.
		 */
		const requestHeaders = new Headers(request.headers);
		const languageHeaders: Negotiator.Headers = {
			"accept-charset": requestHeaders.get("accept-charset") || undefined,
			"accept-encoding": requestHeaders.get("accept-encoding") || undefined,
			"accept-language": requestHeaders.get("accept-language") || undefined,
		};
		const locale = getCurrentLocale(languageHeaders, locales, defaultLocale);
		request.nextUrl.pathname = `/${locale}${pathname}`;
		// Use manual Response instead of Response.redirect() to add Vary header.
		// Response.redirect() returns immutable headers.
		return new Response(null, {
			status: 302,
			headers: {
				Location: request.nextUrl.toString(),
				Vary: "Accept-Language",
			},
		});
	}

	// e.g. incoming request is /products
	// The new URL is now /en-US/products
}

/**
 * Processes locale for "as-needed" mode where the default locale has no URL prefix
 *
 * Behaviors:
 * - /en-US/page (default locale with prefix) -> Redirect to /page
 * - /page (no locale) -> Rewrite internally to /en-US/page
 * - /de/page (non-default locale) -> Pass through normally
 */
function processLocaleAsNeeded(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): Response | NextResponse | void {
	const { pathname = "/" } = request.nextUrl;

	// Check for custom locale mapping first (e.g., /en/* -> /en-US/*)
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const mappingMatch = pathname.match(localeRegex);

		if (mappingMatch) {
			const unsupportedLocale = mappingMatch[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];

			if (targetLocale && locales.includes(targetLocale)) {
				const rest = mappingMatch[2] === "/" ? "" : mappingMatch[2];
				// If mapping to default locale, redirect without prefix
				if (targetLocale === defaultLocale) {
					request.nextUrl.pathname = rest || "/";
					return Response.redirect(request.nextUrl, 308);
				}
				request.nextUrl.pathname = `/${targetLocale}${rest}`;
				return Response.redirect(request.nextUrl, 308);
			}
		}
	}

	// Layer 2: catch globally-known locales that aren't supported by this site
	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		// Map to default locale — in as-needed mode, default has no prefix
		const rest = removePathnameLocale(replacedUnsupportedLocale, [defaultLocale]);
		request.nextUrl.pathname = rest || "/";
		return Response.redirect(request.nextUrl, 308);
	}

	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);

	// Handle lang query parameter
	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam?.length) {
		const localeFromParam = langToLocale(langParam, locales);
		if (typeof localeFromParam === "string") {
			request.nextUrl.searchParams.delete("lang");

			if (localeFromParam === defaultLocale) {
				// Default locale - redirect to path without prefix
				const newPathname = pathnameHasLocale ? removePathnameLocale(pathname, locales) : pathname;
				request.nextUrl.pathname = newPathname;
				return Response.redirect(request.nextUrl);
			} else {
				// Non-default locale - redirect to path with prefix
				const newPathname = pathnameHasLocale ? removePathnameLocale(pathname, locales) : pathname;
				request.nextUrl.pathname = `/${localeFromParam}${newPathname}`;
				return Response.redirect(request.nextUrl);
			}
		}
	}

	if (pathnameHasLocale) {
		const localeFromPath = pathnameToLocale(pathname, locales);

		if (typeof localeFromPath === "string") {
			// If path has default locale prefix, redirect to remove it
			if (localeFromPath.toLowerCase() === defaultLocale.toLowerCase()) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = newPathname || "/";
				return Response.redirect(request.nextUrl, 308);
			}

			// Non-default locale with prefix - fix case if needed and continue
			if (!pathnameStartsWithLocale(pathname, localeFromPath)) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = `/${localeFromPath}${newPathname}`;
				return Response.redirect(request.nextUrl);
			}

			// Non-default locale with correct prefix - continue normally
			return;
		}
	}

	// No locale in pathname - rewrite to default locale internally
	// This keeps the URL as /page but routes to /[lang]/page with lang=defaultLocale
	const rewriteUrl = request.nextUrl.clone();
	rewriteUrl.pathname = `/${defaultLocale}${pathname}`;
	return NextResponse.rewrite(rewriteUrl);
}

/**
 * Resolves an arbitrary locale string to a supported locale.
 *
 * Resolution order:
 * 1. Direct match in `locales` after normalizing `_` → `-` (e.g. `pt_BR` → `pt-BR`)
 * 2. Case-insensitive lookup in `unsupportedLocaleMapping`
 *
 * Returns `undefined` for syntactically invalid values (> 20 chars or containing
 * characters outside `[a-zA-Z0-9_-]`), or when no match is found.
 *
 * @param unknownLocale - Raw locale string from a URL param or cookie
 * @param locales - Array of supported locale codes
 * @param unsupportedLocaleMapping - Optional mapping for legacy/partner locale codes
 */
export function getMappedLocale(
	unknownLocale: string,
	locales: string[],
	unsupportedLocaleMapping?: Record<string, string>,
): string | undefined {
	if (unknownLocale && /^[a-zA-Z0-9_-]{1,20}$/.test(unknownLocale)) {
		// Normalize underscore-format codes from partner sites (e.g. pt_BR → pt-BR)
		const normalizedLangParam = unknownLocale.replace(/_/g, "-");
		// Case-insensitive direct match returns canonical casing (e.g. "es-es" → "es-ES").
		// Partner systems like Fab's /set-language persist locales lowercased.
		const directMatch = locales.find((l) => l.toLowerCase() === normalizedLangParam.toLowerCase());
		if (directMatch) return directMatch;
		if (unsupportedLocaleMapping) {
			const mapped = unsupportedLocaleMapping[normalizedLangParam.toLowerCase()];
			if (mapped && locales.includes(mapped)) return mapped;
		}
	}

	return;
}

/**
 * Determines the best matching locale for a request using the following priority:
 *
 * 1. `lang` query parameter
 * 2. Epic locale cookie (`LocaleCookieName`)
 * 3. `Accept-Language` header negotiation
 * 4. `defaultLocale` as final fallback
 *
 * `lang` and cookie values are resolved via {@link getMappedLocale}, which supports
 * underscore-format codes (e.g. `pt_BR`) and unsupported locale aliases.
 *
 * @param request - The incoming Next.js request
 * @param locales - Array of supported locale codes
 * @param defaultLocale - Fallback locale when no signal matches
 * @param unsupportedLocaleMapping - Optional mapping for legacy/partner locale codes
 */
export function getBestLocaleFromRequest(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): string {
	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam) {
		const mappedLocale = getMappedLocale(langParam, locales, unsupportedLocaleMapping);
		if (mappedLocale) return mappedLocale;
	}

	// The cookie value is intentionally left unchanged — it is the source of truth for the
	// user's expressed locale preference. The middleware maps it to a supported locale for
	// routing; it is the app's responsibility to render content for the resolved locale.
	const epicLangCookie = request.cookies.get(LocaleCookieName)?.value;
	if (epicLangCookie) {
		const mappedLocale = getMappedLocale(epicLangCookie, locales, unsupportedLocaleMapping);
		if (mappedLocale) return mappedLocale;
	}

	const requestHeaders = new Headers(request.headers);
	const languageHeaders: Negotiator.Headers = {
		"accept-charset": requestHeaders.get("accept-charset") || undefined,
		"accept-encoding": requestHeaders.get("accept-encoding") || undefined,
		"accept-language": requestHeaders.get("accept-language") || undefined,
	};
	const resolved = getCurrentLocale(languageHeaders, locales, defaultLocale);
	return resolved || defaultLocale;
}

function getHostname(forwardedHost?: string | null): string | undefined {
	// Allow explicit override (e.g. for domain-based locale routing in non-standard environments)
	try {
		const domainOverride = process.env.NEXT_PUBLIC_EPIC_LOCALE_DOMAIN_OVERRIDE;
		if (typeof domainOverride === "string") {
			const cleanDomain = domainOverride?.trim();
			if (cleanDomain.length > 0) return cleanDomain;
		}
	} catch {}

	// Prefer x-forwarded-host; strip trailing port if present (e.g. "site.com:8080" → "site.com")
	if (typeof forwardedHost === "string" && forwardedHost.length > 0) {
		if (forwardedHost.includes(":")) {
			return forwardedHost.replace(/:\d+$/, "");
		}
		return forwardedHost;
	}

	// Fall back to the server URL env variable
	try {
		const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;
		if (serverUrl) {
			const { hostname } = new URL(serverUrl);
			return hostname || undefined;
		}
	} catch {}

	return;
}
/**
 * Checks for a `setlang` query param, sets a locale cookie with its value,
 * and redirects to the same URL with the param removed.
 *
 * The `setlang` param is always removed from the URL, even when its value is
 * absent, empty, or whitespace-only (those cases skip the cookie and redirect).
 * Returns `void` if the param is absent.
 */
function handleSetLangParam(
	request: NextRequest,
	locales: string[],
	unsupportedLocaleMapping?: Record<string, string>,
): NextResponse | void {
	const rawSetlang = request.nextUrl.searchParams.get("setlang");
	if (rawSetlang === null) return;

	// Always remove the param so it never leaks into downstream redirects
	request.nextUrl.searchParams.delete("setlang");
	const response = NextResponse.redirect(request.nextUrl);

	const setlang = rawSetlang.trim();
	if (!setlang) return response;

	let locale = langToLocale(setlang, locales);

	if (!locale) {
		const mappedLocale = getMappedLocale(setlang, locales, unsupportedLocaleMapping);
		if (mappedLocale) {
			locale = mappedLocale;
		}
	}

	// Set-Cookie only if locale is in locales list
	if (typeof locale === "string") {
		const isSecure = request.nextUrl.protocol === "https:" || request.headers.get("x-forwarded-proto") === "https";
		const forwardedHost = request.headers.get("x-forwarded-host");
		const hostname = getHostname(forwardedHost);
		if (hostname) {
			const domain = getTopLevelDomain(hostname);
			response.cookies.set(LocaleCookieName, locale, {
				path: "/",
				sameSite: "lax",
				domain,
				secure: isSecure,
			});
		}
	}
	return response;
}

/**
 * 308-redirects an underscore-format locale prefix (e.g. /en_US/foo, /pt_BR/foo)
 * to its canonical dash-form equivalent. Partner systems and legacy bookmarks
 * frequently use POSIX-style codes; without this step they bypass the
 * locale-aware pipeline and 404. Mirrors getMappedLocale's underscore handling
 * for `lang` query params and the locale cookie.
 *
 * Length caps {2,3} and {2,4} cover all locales used by Cosmos sites today and
 * stop the regex from matching arbitrary `<word>_<word>` URL segments. The `i`
 * flag is for symmetry with getLocaleMappingRegex — `[a-zA-Z]` already covers casing.
 *
 * Deliberately excludes (falls through to existing pipeline):
 *  - 3-part script tags (zh_Hans_CN) — partners virtually never emit them
 *  - Numeric region codes (es_419)   — `[a-zA-Z]` only, by design
 *  - Bare-language paths (/de, /fr)  — regex requires the underscore
 */
function processUnderscoreLocale(
	request: NextRequest,
	locales: string[],
	unsupportedLocaleMapping?: Record<string, string>,
): Response | void {
	const { pathname = "/" } = request.nextUrl;
	const match = pathname.match(/^\/([a-zA-Z]{2,3})_([a-zA-Z]{2,4})(\/?$|\/.+)/i);
	if (!match) return;

	const candidate = `${match[1]}-${match[2]}`;
	const rest = match[3] === "/" ? "" : match[3];

	// Direct match against site locales (case-insensitive, returns canonical casing)
	const directMatch = locales.find((l) => l.toLowerCase() === candidate.toLowerCase());
	if (directMatch) {
		request.nextUrl.pathname = `/${directMatch}${rest}`;
		return Response.redirect(request.nextUrl, 308);
	}

	// Unsupported-locale mapping (case-insensitive)
	if (unsupportedLocaleMapping) {
		const mapped = unsupportedLocaleMapping[candidate.toLowerCase()];
		if (mapped && locales.includes(mapped)) {
			request.nextUrl.pathname = `/${mapped}${rest}`;
			return Response.redirect(request.nextUrl, 308);
		}
	}
	// No match — fall through; existing pipeline handles unknown prefixes by
	// falling back to defaultLocale.
}

/**
 * Processes locale for "never" mode where no locale prefixes appear in URLs
 * Useful for domain-based routing where each domain serves one locale
 */
function processLocaleNever(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
	siteIdHeaders?: Record<string, string>,
	options?: ProcessLocaleOptions,
): Response | NextResponse | void {
	const { pathname = "/" } = request.nextUrl;

	// Check for unsupported locale prefix (e.g., /pt/* on a site that doesn't support pt)
	// Strip the prefix and redirect; set ?lang to the mapped locale if it's not the default
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const mappingMatch = pathname.match(localeRegex);

		if (mappingMatch) {
			const unsupportedLocale = mappingMatch[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];
			const rest = mappingMatch[2] === "/" ? "" : mappingMatch[2];
			request.nextUrl.pathname = rest || "/";
			if (targetLocale && targetLocale !== defaultLocale && !request.nextUrl.searchParams.has("lang")) {
				request.nextUrl.searchParams.set("lang", targetLocale);
			}
			return Response.redirect(request.nextUrl, 308);
		}
	}

	// Layer 2: catch globally-known locales that aren't supported by this site
	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		// Strip the unsupported locale prefix — in never mode, no locale prefixes appear in URLs
		const rest = removePathnameLocale(replacedUnsupportedLocale, [defaultLocale]);
		request.nextUrl.pathname = rest || "/";
		return Response.redirect(request.nextUrl, 308);
	}

	// If there's a locale prefix, redirect to remove it and preserve locale as ?lang param
	// (only if it's not the default locale — default is already assumed in never mode)
	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);
	if (pathnameHasLocale) {
		const localeFromPath = pathnameToLocale(pathname, locales);
		const newPathname = removePathnameLocale(pathname, locales);
		request.nextUrl.pathname = newPathname || "/";
		if (
			typeof localeFromPath === "string" &&
			localeFromPath !== defaultLocale &&
			!request.nextUrl.searchParams.has("lang")
		) {
			request.nextUrl.searchParams.set("lang", localeFromPath);
		}
		return Response.redirect(request.nextUrl, 308);
	}

	const bestLocale = getBestLocaleFromRequest(request, locales, defaultLocale, unsupportedLocaleMapping);

	// Forward the resolved locale on a request header for sites with no [lang]
	// segment. The URL and the rendered route stay identical, so no rewrite.
	if (options?.localeViaHeader) {
		const requestHeaders = new Headers(request.headers);
		requestHeaders.set(LocaleHeaderName, bestLocale);
		return NextResponse.next({ request: { headers: requestHeaders }, headers: siteIdHeaders });
	}

	// Rewrite to best locale internally so the [lang] segment resolves
	const rewriteUrl = request.nextUrl.clone();
	rewriteUrl.pathname = `/${bestLocale}${pathname}`;
	return NextResponse.rewrite(rewriteUrl, { headers: siteIdHeaders });
}
